// src/pinball/PinballRenderer.ts - Layered Canvas 2D renderer

import {
  TableThemeConfig,
  PinballGameState,
  Vector2D,
  VisualEffect,
  FlipperBody,
} from './types';

export class PinballRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private staticCanvas: HTMLCanvasElement;
  private staticCtx: CanvasRenderingContext2D;
  private tableConfig: TableThemeConfig;
  private scale: number = 1;
  private offsetX: number = 0;
  private offsetY: number = 0;
  private dpr: number = 1;
  private staticDirty: boolean = true;

  constructor(canvas: HTMLCanvasElement, tableConfig: TableThemeConfig) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    this.tableConfig = tableConfig;

    // Create offscreen canvas for static layer
    this.staticCanvas = document.createElement('canvas');
    this.staticCtx = this.staticCanvas.getContext('2d')!;
  }

  initializeLayers(): void {
    this.staticDirty = true;
    this.renderStaticLayer();
  }

  resize(width: number, height: number, dpr: number): void {
    this.dpr = dpr;

    // Calculate scale to fit table in available space maintaining aspect ratio
    const tableAspect = this.tableConfig.width / this.tableConfig.height;
    const canvasAspect = width / height;

    if (canvasAspect > tableAspect) {
      // Canvas is wider - fit to height
      this.scale = height / this.tableConfig.height;
      this.offsetX = (width - this.tableConfig.width * this.scale) / 2;
      this.offsetY = 0;
    } else {
      // Canvas is taller - fit to width
      this.scale = width / this.tableConfig.width;
      this.offsetX = 0;
      this.offsetY = (height - this.tableConfig.height * this.scale) / 2;
    }

    // Set canvas size
    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;
    this.ctx.scale(dpr, dpr);

    // Resize static canvas
    this.staticCanvas.width = width * dpr;
    this.staticCanvas.height = height * dpr;
    this.staticCtx.scale(dpr, dpr);

    this.staticDirty = true;
  }

  renderStaticLayer(): void {
    if (!this.staticDirty) return;

    const ctx = this.staticCtx;
    const theme = this.tableConfig.theme;

    // Clear
    ctx.clearRect(0, 0, this.staticCanvas.width / this.dpr, this.staticCanvas.height / this.dpr);

    // Background
    ctx.fillStyle = theme.backgroundColor;
    ctx.fillRect(0, 0, this.staticCanvas.width / this.dpr, this.staticCanvas.height / this.dpr);

    // Playfield
    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    ctx.fillStyle = theme.playfieldColor;
    ctx.fillRect(0, 0, this.tableConfig.width, this.tableConfig.height);

    // Draw walls
    ctx.strokeStyle = theme.wallColor;
    ctx.lineWidth = 4;
    ctx.lineCap = 'round';
    for (const wall of this.tableConfig.walls) {
      ctx.beginPath();
      ctx.moveTo(wall.start.x, wall.start.y);
      ctx.lineTo(wall.end.x, wall.end.y);
      ctx.stroke();
    }

    // Draw static elements
    for (const element of this.tableConfig.elements) {
      const color = element.properties.color || '#ffffff';

      switch (element.type) {
        case 'bumper':
        case 'pop_bumper': {
          const radius = element.properties.radius || 15;
          ctx.beginPath();
          ctx.arc(element.position.x, element.position.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();
          ctx.strokeStyle = '#ffffff';
          ctx.lineWidth = 2;
          ctx.stroke();
          break;
        }
        case 'target':
        case 'drop_target': {
          const w = element.properties.width || 20;
          ctx.fillStyle = color;
          ctx.fillRect(element.position.x - w / 2, element.position.y - 3, w, 6);
          break;
        }
        case 'slingshot': {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(element.position.x, element.position.y);
          ctx.lineTo(
            element.position.x + (element.properties.width || 40),
            element.position.y + (element.properties.height || 40)
          );
          ctx.lineTo(element.position.x, element.position.y + (element.properties.height || 40));
          ctx.closePath();
          ctx.fill();
          break;
        }
        case 'spinner': {
          const w = element.properties.width || 30;
          ctx.strokeStyle = color;
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(element.position.x - w / 2, element.position.y);
          ctx.lineTo(element.position.x + w / 2, element.position.y);
          ctx.stroke();
          break;
        }
        case 'ramp': {
          if (element.properties.path) {
            ctx.strokeStyle = color;
            ctx.lineWidth = 3;
            ctx.setLineDash([5, 5]);
            ctx.beginPath();
            const path = element.properties.path;
            ctx.moveTo(path[0].x, path[0].y);
            for (let i = 1; i < path.length; i++) {
              ctx.lineTo(path[i].x, path[i].y);
            }
            ctx.stroke();
            ctx.setLineDash([]);
          }
          break;
        }
        case 'lane': {
          if (element.properties.path) {
            ctx.strokeStyle = color;
            ctx.lineWidth = 2;
            ctx.beginPath();
            const path = element.properties.path;
            ctx.moveTo(path[0].x, path[0].y);
            for (let i = 1; i < path.length; i++) {
              ctx.lineTo(path[i].x, path[i].y);
            }
            ctx.stroke();
          }
          break;
        }
        default:
          break;
      }
    }

    // Draw plunger area (just the plunger indicator, no lane walls — physics walls handle that)
    const plunger = this.tableConfig.plunger;
    ctx.fillStyle = '#333333';
    ctx.fillRect(plunger.position.x - 8, plunger.position.y - 5, 16, 10);

    // Draw drain area
    ctx.fillStyle = '#330000';
    ctx.fillRect(
      this.tableConfig.drain.position.x - this.tableConfig.drain.width / 2,
      this.tableConfig.drain.position.y - 5,
      this.tableConfig.drain.width,
      10
    );

    ctx.restore();
    this.staticDirty = false;
  }

  renderDynamicLayer(state: PinballGameState, alpha: number): void {
    const ctx = this.ctx;

    // Clear and draw static layer
    ctx.clearRect(0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);

    if (this.staticDirty) {
      this.renderStaticLayer();
    }
    ctx.drawImage(this.staticCanvas, 0, 0, this.canvas.width / this.dpr, this.canvas.height / this.dpr);

    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    // Draw drop targets (show lowered state)
    for (const element of this.tableConfig.elements) {
      if (element.type === 'drop_target' && state.dropTargetStates[element.id]) {
        const w = element.properties.width || 20;
        ctx.fillStyle = '#333333';
        ctx.fillRect(element.position.x - w / 2, element.position.y - 3, w, 6);
      }
    }

    // Draw flippers
    const theme = this.tableConfig.theme;
    for (const flipperConfig of this.tableConfig.flippers) {
      const isActive = flipperConfig.side === 'left' ? state.leftFlipperActive : state.rightFlipperActive;
      const angle = isActive ? flipperConfig.activeAngle : flipperConfig.restAngle;

      ctx.save();
      ctx.translate(flipperConfig.pivot.x, flipperConfig.pivot.y);
      ctx.rotate(angle);

      ctx.fillStyle = theme.flipperColor;
      ctx.beginPath();
      ctx.ellipse(0, 0, 8, 5, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillRect(0, -4, flipperConfig.length, 8);

      ctx.beginPath();
      ctx.ellipse(flipperConfig.length, 0, 5, 4, 0, 0, Math.PI * 2);
      ctx.fill();

      ctx.restore();
    }

    // Draw balls
    for (const ball of state.balls) {
      if (!ball.isActive) continue;

      ctx.beginPath();
      ctx.arc(ball.position.x, ball.position.y, 10, 0, Math.PI * 2);
      ctx.fillStyle = theme.ballColor;
      ctx.fill();
      ctx.strokeStyle = '#666666';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Motion blur effect
      const speed = Math.sqrt(ball.velocity.x ** 2 + ball.velocity.y ** 2);
      if (speed > 200) {
        const blurLength = Math.min(speed / 50, 20);
        const nx = -ball.velocity.x / speed;
        const ny = -ball.velocity.y / speed;
        ctx.beginPath();
        ctx.moveTo(ball.position.x + nx * 10, ball.position.y + ny * 10);
        ctx.lineTo(ball.position.x + nx * blurLength, ball.position.y + ny * blurLength);
        ctx.strokeStyle = `rgba(200, 200, 200, 0.3)`;
        ctx.lineWidth = 8;
        ctx.stroke();
      }
    }

    // Draw plunger
    if (state.ballInLauncher) {
      const plunger = this.tableConfig.plunger;
      const plungerY = plunger.position.y + state.plungerPower * 30;
      ctx.fillStyle = '#888888';
      ctx.fillRect(plunger.position.x - 8, plungerY, 16, 20);

      // Power meter
      if (state.plungerCharging) {
        ctx.fillStyle = `hsl(${120 - state.plungerPower * 120}, 100%, 50%)`;
        ctx.fillRect(
          plunger.position.x - plunger.laneWidth / 2 - 8,
          plunger.position.y - plunger.laneHeight,
          6,
          plunger.laneHeight * state.plungerPower
        );
      }
    }

    ctx.restore();
  }

  renderEffectsLayer(effects: VisualEffect[]): void {
    const ctx = this.ctx;
    const now = Date.now();

    ctx.save();
    ctx.translate(this.offsetX, this.offsetY);
    ctx.scale(this.scale, this.scale);

    for (const effect of effects) {
      const elapsed = now - effect.startTime;
      const progress = Math.min(elapsed / effect.duration, 1);

      if (progress >= 1) continue;

      switch (effect.type) {
        case 'flash': {
          const alpha = 1 - progress;
          const radius = 20 + progress * 30;
          ctx.beginPath();
          ctx.arc(effect.position.x, effect.position.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha * 0.6})`;
          ctx.fill();
          break;
        }
        case 'score_popup': {
          const alpha = 1 - progress;
          const y = effect.position.y - progress * 40;
          ctx.fillStyle = `rgba(255, 255, 0, ${alpha})`;
          ctx.font = 'bold 14px sans-serif';
          ctx.textAlign = 'center';
          const text = effect.data?.points?.toString() || '';
          ctx.fillText(text, effect.position.x, y);
          break;
        }
        case 'celebration': {
          const alpha = 1 - progress;
          ctx.fillStyle = `rgba(255, 215, 0, ${alpha * 0.3})`;
          ctx.fillRect(0, 0, this.tableConfig.width, this.tableConfig.height);
          ctx.fillStyle = `rgba(255, 255, 255, ${alpha})`;
          ctx.font = 'bold 32px sans-serif';
          ctx.textAlign = 'center';
          ctx.fillText('JACKPOT!', this.tableConfig.width / 2, this.tableConfig.height / 2);
          break;
        }
        case 'glow': {
          const alpha = 0.3 + Math.sin(progress * Math.PI * 4) * 0.3;
          ctx.beginPath();
          ctx.arc(effect.position.x, effect.position.y, 15, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 255, 128, ${alpha})`;
          ctx.fill();
          break;
        }
        default:
          break;
      }
    }

    ctx.restore();
  }

  worldToScreen(point: Vector2D): Vector2D {
    return {
      x: point.x * this.scale + this.offsetX,
      y: point.y * this.scale + this.offsetY,
    };
  }

  screenToWorld(point: Vector2D): Vector2D {
    return {
      x: (point.x - this.offsetX) / this.scale,
      y: (point.y - this.offsetY) / this.scale,
    };
  }
}
